import React, { useMemo } from 'react';
import {
  VehicleViewProps,
  PaintPointNumber,
} from './VehiclePaintPoints.types';
import { SedanPaintSVG } from './SedanPaintSVG';
import { VanPaintSVG } from './VanPaintSVG';
import { HatchbackPaintSVG } from './HatchbackPaintSVG';
import { PaintPointLabel } from './PaintPointLabel';
import { getVehiclePaintPointsMap } from './paintPointsMapping';
import { getVehiclePaintPointsSvgStyle } from '../shared/vehicleSvgSizeMap';
import clsx from 'clsx';

/**
 * Komponent pojedynczego widoku pojazdu z punktami pomiarowymi lakieru
 *
 * Logika działania:
 * 1. Pobiera mapowanie punktów dla danego typu pojazdu
 * 2. Iteruje po wszystkich możliwych numerach punktów (12-32)
 * 3. Pokazuje punkt TYLKO jeśli ma zdefiniowane współrzędne dla tego widoku
 * 4. Wartość i kolor pobiera z API (props.points)
 * 5. Jeśli brak danych z API, wyświetla punkt jako MISSING
 */
export const VehicleView: React.FC<VehicleViewProps> = ({
  view,
  vehicleType,
  points,
  label,
  heightMobile,
  highlightedPoint = null,
  onPointHover,
  onPointClick,
  onVehicleSvgLoad,
}) => {
  // Wybór komponentu SVG na podstawie typu pojazdu
  const VehicleSVG =
    vehicleType === 'sedan'
      ? SedanPaintSVG
      : vehicleType === 'van'
        ? VanPaintSVG
        : HatchbackPaintSVG;

  // Pobierz mapowanie punktów dla tego typu pojazdu
  const pointsMap = useMemo(
    () => getVehiclePaintPointsMap(vehicleType),
    [vehicleType],
  );

  // Utwórz mapę numer -> wartość dla szybkiego wyszukiwania
  // Używamy stringów jako kluczy dla normalizacji (nameId jest stringiem)
  const pointDataMap = useMemo(() => {
    const map = new Map<string, { value?: number; minMeasurement?: number; maxMeasurement?: number }>();
    points.forEach((point) => {
      const normalizedNameId = String(point.nameId);
      map.set(normalizedNameId, {
        value: point.value,
        minMeasurement: point.minMeasurement,
        maxMeasurement: point.maxMeasurement,
      });
    });
    return map;
  }, [points]);

  const pointsToDisplay = useMemo(() => {
    const viewPointsMap = pointsMap[view];

    return Object.entries(viewPointsMap)
      .filter(([, coords]) => coords !== undefined)
      .map(([pointNumber, coords]) => {
        const normalizedPointNumber = String(pointNumber);
        const pointData = pointDataMap.get(normalizedPointNumber);

        return {
          pointNumber: normalizedPointNumber as PaintPointNumber,
          coords: coords!,
          value: pointData?.value,
          minMeasurement: pointData?.minMeasurement,
          maxMeasurement: pointData?.maxMeasurement,
        };
      });
  }, [pointsMap, view, pointDataMap]);

  return (
    <div className="flex flex-col gap-3 self-stretch">
      {/* Etykieta widoku */}
      <div className={clsx(' text-xs font-normal text-blue-300', label?.length > 0 ? 'pt-0' : 'pt-4', {
        'lg:text-left pl-10 lg:pl-0': view === 'left',
        'text-right lg:text-right pr-10 lg:pr-0': view === 'right',
      })}>{label}</div>

      {/* SVG pojazdu z punktami */}
      <div className="vehicle-svg-container relative mx-auto w-full"
        style={{
          ...getVehiclePaintPointsSvgStyle(vehicleType, view),
          ...(heightMobile ? { '--vehicle-svg-h': `${heightMobile}px`, '--vehicle-svg-h-lg': `${heightMobile}px` } as React.CSSProperties : {}),
        }}
      >
        {/* Sylwetka pojazdu */}
        <VehicleSVG view={view} className="w-full h-full" onLoad={onVehicleSvgLoad} />

        {/* Overlay z etykietami punktów */}
        <div className="absolute inset-0 w-full h-full">
          {pointsToDisplay.map((point) => (
            <PaintPointLabel
              key={point.pointNumber}
              pointNumber={point.pointNumber}
              value={point.value}
              x={point.coords.x}
              y={point.coords.y}
              position={point.coords.position}
              labelOverride={point.coords.labelOverride}
              hideLabel={point.coords.hideLabel}
              minMeasurement={point.minMeasurement}
              maxMeasurement={point.maxMeasurement}
              isHighlighted={highlightedPoint === String(point.pointNumber)}
              onHover={onPointHover}
              onClick={onPointClick}
            />
          ))}
        </div>
      </div>
    </div >
  );
};
